// ============================================================
//  SliderSection.jsx — COMPLETO 100% fiel ao Preview do Admin
// ============================================================

import React, { useState, useEffect, useRef } from "react";
import api from "../../api/api";
import { Link } from "react-router-dom";
import "../../styles/sliderAnimations.css"; // <<< obrigatório

const BACKEND_URL = "https://imagensdaface.com.br";
const TRANSITION_DURATION = 800; // ms — mesmo do admin

export default function SliderSection() {
    const [slides, setSlides] = useState([]);
    const [index, setIndex] = useState(0);

    const prevIndex = useRef(null);
    const currentRef = useRef(null);
    const previousRef = useRef(null);
    const textRef = useRef(null);

    const autoTimer = useRef(null);

    // ================================
    //  Carrega slides publicados
    // ================================
    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get("/slider");

                const data = (res.data?.slides || []).map(s => ({
                    ...s,
                    full_image: s.image_url
                        ? (s.image_url.startsWith("http") ? s.image_url : `${BACKEND_URL}/${s.image_url}`)
                        : null,
                    duration_ms: Number(s.duration_ms) || 5000,
                    text_animation_duration_ms: Number(s.text_animation_duration_ms) || 500,
                    border_radius_px: Number(s.border_radius_px) || 16,
                    overlay_opacity: Number(s.overlay_opacity ?? 0.35),
                    animation_type: s.animation_type || "none",
                    image_fit: s.image_fit || "cover",
                }));

                setSlides(data);
            } catch (e) {
                console.error("Erro carregando slider:", e);
            }
        };

        load();
    }, []);


    // ================================
    //  Autoplay com sincronização
    // ================================
    useEffect(() => {
        if (slides.length === 0) return;

        clearTimeout(autoTimer.current);

        autoTimer.current = setTimeout(() => {
            prevIndex.current = index;
            setIndex(i => (i + 1) % slides.length);
        }, slides[index]?.duration_ms || 5000);

        return () => clearTimeout(autoTimer.current);
    }, [index, slides]);


    // ================================
    //  Executa Transição como no Admin Preview
    // ================================
    useEffect(() => {
        if (!currentRef.current || !previousRef.current || prevIndex.current === null) return;

        const slide = slides[index];
        const effect = slide.transition_effect;

        const current = currentRef.current;
        const prev = previousRef.current;

        // ================= Pré-Configuração =================
        let enter = "translateX(0)";
        let start = "translateX(100%)";
        let exit = "translateX(-100%)";

        switch (effect) {
            case "fade":
                start = "scale(1)"; exit = "scale(.8)";
                break;
            case "slide-right":
                start = "translateX(-100%)"; exit = "translateX(100%)";
                break;
            case "zoom-in":
                start = "scale(1.3)"; exit = "scale(.7)";
                break;
            case "cube":
                start = "rotateY(90deg)"; exit = "rotateY(-90deg)";
                break;
            case "flip":
                start = "rotateY(180deg)"; exit = "rotateY(-180deg)";
                break;
            case "stack":
                start = "scale(1.07) translateY(6%)"; exit = "scale(.92)";
                break;
        }

        // ================= Reset de estado =================
        current.style.transition = "none";
        current.style.opacity = effect === "fade" ? 0 : 1;
        current.style.transform = start;
        prev.style.transition = "none";
        prev.style.opacity = 1;
        prev.style.transform = "translateX(0)";

        void current.offsetWidth; // força reflow

        // ========= Slide entra =========
        current.style.transition = `opacity ${TRANSITION_DURATION}ms ease, transform ${TRANSITION_DURATION}ms ease`;
        current.style.opacity = 1;
        current.style.transform = enter;

        // ========= Slide sai =========
        prev.style.transition = `opacity ${TRANSITION_DURATION}ms ease, transform ${TRANSITION_DURATION}ms ease`;
        prev.style.opacity = effect === "fade" ? 0 : .4;
        prev.style.transform = exit;

        // ========== ANIMAÇÃO DO TEXTO ==========
        const txt = textRef.current;
        if (txt) {
            const anim = slide.animation_type;
            txt.style.opacity = 0;
            txt.className = "text-wrapper"; // reset classes

            void txt.offsetWidth;

            if (anim !== "none")
                txt.classList.add("animate-text-" + anim);

            txt.style.setProperty("--dur", slide.text_animation_duration_ms + "ms");

            setTimeout(() => txt.style.opacity = 1, TRANSITION_DURATION * .4);
        }

    }, [index, slides]);


    if (!slides.length) return null;

    const slide = slides[index];
    const radius = slide.border_radius_px + "px";

    return (
        <div
            className="relative w-full overflow-hidden shadow-xl my-10"
            style={{ height:"23rem", borderRadius:radius, background:"#000" }}
        >

            {/* ====== SLIDES ====== */}
            {slides.map((s, i) => (
                <img
                    key={i}
                    ref={i === index ? currentRef : (i === prevIndex.current ? previousRef : null)}
                    src={s.full_image}
                    alt=""
                    className="absolute inset-0 w-full h-full"
                    style={{
                        objectFit: s.image_fit,
                        borderRadius:radius,
                        zIndex:i===index?5:i===prevIndex.current?4:1
                    }}
                />
            ))}

            {/* ====== OVERLAY ====== */}
            <div
                className="absolute inset-0 z-10"
                style={{
                    backgroundColor: slide.overlay_color,
                    opacity: slide.overlay_opacity,
                    borderRadius:radius
                }}
            />

            {/* ====== TEXTO ====== */}
            <div className="absolute inset-0 flex items-center px-10 z-20">
                <div ref={textRef} className="text-wrapper max-w-2xl text-white drop-shadow-2xl">

                    {slide.display_text && (
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                            {slide.display_text}
                        </h2>
                    )}

                    {(slide.subtitle || slide.title) && (
                        <p
                            className="mb-3"
                            style={{
                                color: slide.subtitle_color,
                                fontSize:slide.subtitle_size+"px",
                                fontFamily:slide.subtitle_font
                            }}
                        >
                            {slide.subtitle || slide.title}
                        </p>
                    )}

                    {slide.link_url && (
                        <Link to={slide.link_url}
                            className="px-6 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 shadow">
                            {slide.button_text || "Ver mais"}
                        </Link>
                    )}
                </div>
            </div>

            {/* ====== DOTS ====== */}
            <div className="absolute bottom-4 right-6 flex gap-2 z-40">
                {slides.map((_, i) => (
                    <button key={i} onClick={() => {
                        prevIndex.current = index; setIndex(i);
                    }} className={`w-3 h-3 rounded-full ${
                        i===index?"bg-white scale-110":"bg-white/40"
                    }`} />
                ))}
            </div>
        </div>
    );
}
